import React from "react";
import {
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDataFunctions } from "contexts/Data";
import { LoadingBox } from "../Loading";

export default function QueueManager() {
  const dataFunctions = useDataFunctions();

  const [loading, setLoading] = React.useState(true);
  const [queues, setQueues] = React.useState([]);
  const [filteredQueues, setFilteredQueues] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  // Members‐modal state
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalMembers, setModalMembers] = React.useState([]);
  const [modalQueueName, setModalQueueName] = React.useState("");

  // Escalation‐paths modal state
  const [escModalOpen, setEscModalOpen] = React.useState(false);
  const [escPaths, setEscPaths] = React.useState([]);
  const [escQueueId, setEscQueueId] = React.useState(null)
  const [escQueueName, setEscQueueName] = React.useState("");

  // Fetch data once
  React.useEffect(() => {
    dataFunctions.getQueueManagement()
      .then(({ queues: qMap = {}, users: uMap = {} }) => {
        const userList = Object.values(uMap);
        const queueList = Object.values(qMap).map(q => ({
          ...q,
          members: Array.isArray(q.members) ? q.members : [],
          membersCount: Array.isArray(q.members) ? q.members.length : 0,
          managerName: uMap[q.manager_sys_id]?.name || "Unknown",
          // keep full paths array, but also count for the grid
          escalation_paths: q.escalation_paths || [],
          escalation_paths_count: (q.escalation_paths || []).length
        }));
        setUsers(userList);
        setQueues(queueList);
        setFilteredQueues(queueList);
        setLoading(false);
      })
      .catch(console.error);
  }, []);

  // Filter by selected users
  React.useEffect(() => {
    const ids = selectedUsers.map(u => u.sys_id);
    const updated = ids.length
      ? queues.filter(q => q.members.some(m => ids.includes(m)))
      : queues;
    setFilteredQueues(updated);
  }, [selectedUsers, queues]);

  // Members modal handlers
  const handleOpenMembers = (membersArray, queueName) => {
    const details = membersArray
      .map(id => users.find(u => u.sys_id === id))
      .filter(Boolean);
    setModalMembers(details);
    setModalQueueName(queueName);
    setModalOpen(true);
  };
  const handleClose = () => setModalOpen(false);

  // Escalation‐paths modal handlers
  const handleOpenEscalations = (paths, queueName, queueId) => {
    setEscPaths(paths.map(p => ({ ...p })));
    setEscQueueName(queueName);
    setEscQueueId(queueId);
    setEscModalOpen(true);
  };
  const handleSavePath = (path_data) => {
    console.log("Saving path", path_data);
  };

  const handleDeletePath = (path_data) => {
    console.log("Deleting path", path_data);
    // then optimistically remove it from UI
    setEscPaths(paths => paths.filter(p => p !== path_data));    
  };

  return (
    <LoadingBox loading={loading} message={"Loading Queue Manager"} sx={{ width: "100%", height: "100%" }}>
      {/* Search and filter */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          label="Search Queues"
          size="small"
          variant="outlined"
          sx={{ flex: 1 }}
          onChange={({ target: { value } }) => {
            const term = value.toLowerCase();
            setFilteredQueues(
              queues.filter(({ name }) =>
                name.toLowerCase().includes(term)
              )
            );
          }}
        />
        <Autocomplete
          multiple
          options={users}
          getOptionLabel={({ name }) => name}
          filterSelectedOptions
          value={selectedUsers}
          onChange={(_, newVals) => setSelectedUsers(newVals)}
          renderOption={(props, opt) => (
            <MenuItem {...props} key={opt.sys_id} value={opt}>
              <Box>
                <Typography>{opt.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {opt.email}
                </Typography>
              </Box>
            </MenuItem>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Filter by User"
              size="small"
              placeholder="Type to search"
            />
          )}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Queues grid */}
      <Typography variant="h6" gutterBottom>
        Queues
      </Typography>
      {/* <Box sx={{ height: "90%", width: "100%" }}> */}
      {/* all but the top for the filters */}
      <Box sx={{ height: "80vh", width: "100%" }}>
        <DataGrid
          rows={filteredQueues}
          getRowId={({ sys_id }) => sys_id}
          pageSize={200}
          rowsPerPageOptions={[100, 200, 500]}
          components={{ Toolbar: GridToolbar }}
          columns={[
            { field: "name", headerName: "Name", width: 150 },
            { field: "description", headerName: "Description", width: 150 },
            {
              field: "sys_id",
              headerName: "Sys ID",
              // width: 150,
              renderCell: ({ value }) => (
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(value);
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              )
            },
            { field: "managerName", headerName: "Manager", width: 150 },
            {
              field: "membersCount",
              headerName: "Members",
              // width: 90,
              type: "number",
              renderCell: ({ row, value }) => (
                <Typography
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() =>
                    handleOpenMembers(row.members, row.name)
                  }
                >
                  {value}
                </Typography>
              )
            },
            {
              // now uses your count field
              field: "escalation_paths_count",
              headerName: "Responsibilities",
              width: 120,
              type: "number",
              renderCell: ({ row, value }) => (
                <Typography
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() =>
                    handleOpenEscalations(
                      row.escalation_paths,
                      row.name,
                      row.sys_id
                    )
                  }
                >
                  {value}
                </Typography>
              )
            }
          ]}
        />
      </Box>

      {/* Members Modal */}
      <Dialog open={modalOpen} onClose={handleClose} fullWidth>
        <DialogTitle>Members of {modalQueueName}</DialogTitle>
        <DialogContent dividers>
          <List>
            {/* {modalMembers.map(({ sys_id, name, email }) => (
              <ListItem key={sys_id}>
                <ListItemText primary={name} secondary={email} />
              </ListItem>
            ))} */}
            {/* I want to make the email address easily copyable */}
            {modalMembers.map(({ sys_id, name, email }) => (
              <ListItem key={sys_id}>
                <ListItemText primary={name} secondary={email} />
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(email);
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </ListItem>
            ))}
            {/* If no members, show a message */}
            {modalMembers.length === 0 && (
              <Typography>No members to display.</Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Escalation-Paths Modal */}
      <Dialog open={escModalOpen} onClose={() => setEscModalOpen(false)} fullWidth>
        <DialogTitle>Edit Escalation Paths for “{escQueueName}”</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {escPaths.map((path, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              <TextField
                fullWidth
                label={`Path #${i + 1}`}
                variant="outlined"
                value={path.description}
                onChange={e => {
                  const newDesc = e.target.value;
                  setEscPaths(ps =>
                    ps.map((p, idx) =>
                      idx === i ? { ...p, description: newDesc } : p
                    )
                  );
                }}
              />
              <IconButton
                aria-label="save"
                onClick={() => handleSavePath(path)}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                aria-label="delete"
                onClick={() => handleDeletePath(path)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Button
            onClick={() => setEscPaths(ps => [...ps, { description: "" }])}
          >
            Add Path
          </Button>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEscModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </LoadingBox>
  );
}
